// src/configurator/TemplateConfigurator.js - Fixed ESLint Issues
import React, { useState } from 'react';
import {
  Eye,
  Palette,
  Save,
  RefreshCw, // Added missing import
  CheckCircle,
  Settings,
} from 'lucide-react';

const TemplateConfigurator = ({ setCurrentPage }) => {
  const [config, setConfig] = useState({
    companyName: 'Your Company',
    primaryColor: '#3B82F6',
    backgroundColor: '#F9FAFB',
    logoUrl: '',
    customMessage: 'Welcome! Check your project status below.',
    showEstimatedCompletion: true,
    showPriority: true,
    showNotes: true,
    allowClientUpdates: false,
  });

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Simulate API save
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Error saving template config:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleConfigChange = (key, value) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Template Configurator
          </h1>
          <div className="flex items-center space-x-3">
            {saved && (
              <div className="flex items-center text-green-600">
                <CheckCircle className="w-5 h-5 mr-2" />
                <span className="text-sm font-medium">Saved!</span>
              </div>
            )}
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? (
                <>
                  <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5 mr-2" />
                  Save Template
                </>
              )}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Configuration Panel */}
          <div className="space-y-6">
            {/* Basic Settings */}
            <div className="bg-white p-6 rounded-lg shadow border">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Settings className="w-5 h-5 mr-2" />
                Basic Settings
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name
                  </label>
                  <input
                    type="text"
                    value={config.companyName}
                    onChange={(e) =>
                      handleConfigChange('companyName', e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Your Company Name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Welcome Message
                  </label>
                  <textarea
                    value={config.customMessage}
                    onChange={(e) =>
                      handleConfigChange('customMessage', e.target.value)
                    }
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Welcome message for your clients..."
                  />
                </div>
              </div>
            </div>

            {/* Appearance */}
            <div className="bg-white p-6 rounded-lg shadow border">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Palette className="w-5 h-5 mr-2" />
                Appearance
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Primary Color
                  </label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="color"
                      value={config.primaryColor}
                      onChange={(e) =>
                        handleConfigChange('primaryColor', e.target.value)
                      }
                      className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={config.primaryColor}
                      onChange={(e) =>
                        handleConfigChange('primaryColor', e.target.value)
                      }
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Logo URL (optional)
                  </label>
                  <input
                    type="url"
                    value={config.logoUrl}
                    onChange={(e) =>
                      handleConfigChange('logoUrl', e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="https://example.com/logo.png"
                  />
                </div>
              </div>
            </div>

            {/* Features */}
            <div className="bg-white p-6 rounded-lg shadow border">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Display Options
              </h2>

              <div className="space-y-3">
                {[
                  {
                    key: 'showPriority',
                    label: 'Show Priority/Position',
                    desc: 'Display queue position to clients',
                  },
                  {
                    key: 'showEstimatedCompletion',
                    label: 'Show Estimated Completion',
                    desc: 'Display project timeline',
                  },
                  {
                    key: 'showNotes',
                    label: 'Show Notes',
                    desc: 'Display additional project notes',
                  },
                  {
                    key: 'allowClientUpdates',
                    label: 'Allow Client Updates',
                    desc: 'Let clients update their information',
                  },
                ].map((feature) => (
                  <label
                    key={feature.key}
                    className="flex items-start space-x-3 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={config[feature.key]}
                      onChange={(e) =>
                        handleConfigChange(feature.key, e.target.checked)
                      }
                      className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {feature.label}
                      </div>
                      <div className="text-xs text-gray-500">
                        {feature.desc}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Preview Panel */}
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow border">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Eye className="w-5 h-5 mr-2" />
                Live Preview
              </h2>

              <div
                className="border rounded-lg p-4 min-h-96"
                style={{ backgroundColor: config.backgroundColor }}
              >
                <div className="text-center mb-6">
                  {config.logoUrl && (
                    <img
                      src={config.logoUrl}
                      alt="Logo"
                      className="h-12 mx-auto mb-4"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  )}
                  <h1
                    className="text-2xl font-bold mb-2"
                    style={{ color: config.primaryColor }}
                  >
                    {config.companyName}
                  </h1>
                  <p className="text-gray-600 text-sm">
                    {config.customMessage}
                  </p>
                </div>

                {/* Sample Client Card */}
                <div className="bg-white rounded-lg p-4 shadow-sm border">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-900">
                      Sample Client
                    </h3>
                    <span
                      className="px-2 py-1 text-xs font-medium rounded-full text-white"
                      style={{ backgroundColor: config.primaryColor }}
                    >
                      In Progress
                    </span>
                  </div>

                  <div className="space-y-2 text-sm">
                    {config.showPriority && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Queue Position:</span>
                        <span className="font-medium">#3</span>
                      </div>
                    )}

                    {config.showEstimatedCompletion && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Est. Completion:</span>
                        <span className="font-medium">June 15, 2025</span>
                      </div>
                    )}

                    {config.showNotes && (
                      <div className="mt-3">
                        <span className="text-gray-600 text-xs">Notes:</span>
                        <p className="text-gray-800 text-sm mt-1">
                          Project is progressing well. Currently in review
                          phase.
                        </p>
                      </div>
                    )}
                  </div>

                  {config.allowClientUpdates && (
                    <button
                      className="mt-3 w-full py-2 text-sm font-medium rounded border text-white"
                      style={{ backgroundColor: config.primaryColor }}
                    >
                      Update Information
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="mt-8 flex justify-center space-x-4">
          <button
            onClick={() => setCurrentPage('dashboard')}
            className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplateConfigurator;
